/**
 * GDSCardRenderer.js
 * Handles all HTML generation for product cards, headers, and associated UI elements.
 */
import { getSizedShopifyUrl, formatMoney, CARD_IMAGE_DESIRED_WIDTH, CARD_IMAGE_DESIRED_HEIGHT } from './GDSModules.js';

export class GDSCardRenderer {
    constructor(manager, modalManager) {
        if (!manager) {
            throw new Error('GDSCardRenderer: manager is required');
        }

        this.manager = manager;

        // Defensive check for modalManager
        if (!modalManager) {
            console.warn('GDSCardRenderer: modalManager not provided');
            // Create a safe fallback
            this.modalManager = {
                createProductModalHTML: () => {
                    console.warn('modalManager not available - modal creation skipped');
                    return '';
                }
            };
        } else {
            this.modalManager = modalManager;
        }
    }

    createProductCardHTML(product, cardTitle, cardData, isMainCardGroup, isAdditionalCard, cardIndex, initialSelections) {
        try {
            // Validate required parameters
            if (!product || !cardTitle) {
                console.error('createProductCardHTML: Missing required parameters', { product, cardTitle });
                return '';
            }

            const variant = product.variants?.[0];
            const isAvailable = variant?.available || false;
            const isUnavailable = !isAvailable;
            const rawImageUrl = product.featured_image || 'placeholder_image_url.jpg';
            const imageUrl = getSizedShopifyUrl(rawImageUrl, CARD_IMAGE_DESIRED_WIDTH, CARD_IMAGE_DESIRED_HEIGHT);
            const priceInCents = product.price || 0;
            const formattedPrice = formatMoney(priceInCents);
            const price = priceInCents / 100;
            const sku = variant?.sku || product.handle || 'N/A';
            const variantId = variant?.id || '';
            const modalId = `modal-${product.id}-${cardTitle.replace(/\s+/g, '-')}-${cardIndex}-${Math.random().toString(36).substring(2, 7)}`;
            const isSelected = isMainCardGroup && !isUnavailable;
            const borderStyle = isSelected ? 'border: 3px solid #00B588;' : 'border: 1px solid #dcdcdc;';

            const isErgoTypeCard = cardTitle && (
                cardTitle.toLowerCase().includes('standard ergo') ||
                cardTitle.toLowerCase().includes('ergo accessory') ||
                cardTitle.trim().toLowerCase().replace(/\s+/g, ' ') === 'heavy duty gds® ergo™ group-1' ||
                cardTitle.toLowerCase().includes('light duty shoulder strap')
            );

            // Safely check for visible options
            const showEditButton = (cardIndex === 0) &&
                                  !isErgoTypeCard &&
                                  this.manager.dataService &&
                                  typeof this.manager.dataService.hasVisibleOptions === 'function' &&
                                  this.manager.dataService.hasVisibleOptions(cardData);

            const previewEyeIcon = this.generatePreviewEyeIcon(product);

            let attributes = `
                class="bg-white shadow-sm rounded-3xl relative product-card hover:shadow-lg ${isUnavailable ? 'unavailable-product' : ''}"
                data-product-id="${product.id}"
                data-variant-id="${variantId}"
                data-product-handle="${product.handle}"
                data-card-title="${cardTitle}"
                data-product-price="${price.toFixed(2)}"
                data-variant-available="${isAvailable}"
                data-modal-id="${modalId}"
                data-card-index="${cardIndex}"
                ${isAdditionalCard ? 'data-additional-card="true"' : ''}
                ${isSelected ? 'data-selected="true"' : ''}
                style="${borderStyle}"
                onmouseenter="this.style.boxShadow='0 0 15px 3px rgba(0, 0, 0, 0.2)';"
                onmouseleave="this.style.boxShadow='0 0 5px 0 rgba(0, 0, 0, 0.08)';"`;

            if ((isMainCardGroup || isAdditionalCard) && cardIndex > 0) {
                attributes += ` data-secondary-index="${cardIndex}"`;
            }

            if (initialSelections && typeof initialSelections === 'object' && Object.keys(initialSelections).length > 0) {
                const selectionsJson = JSON.stringify(initialSelections);
                attributes += ` data-selections='${selectionsJson.replace(/'/g, '&apos;')}'`;
            }

            let cardHtml = `<div ${attributes}>`;

            if (isUnavailable) {
                cardHtml += `
                    <div class="absolute inset-0 z-15 hidden md:flex items-center justify-center rounded-3xl pointer-events-none coming-soon-overlay">
                        <div class="coming-soon-badge text-white px-4 py-2 rounded-full transform -rotate-12 font-bold text-lg">Coming Soon</div>
                    </div>`;
            }

            cardHtml += this.buildMobileCardView(product, imageUrl, sku, formattedPrice, price, isSelected, isUnavailable, showEditButton, previewEyeIcon, modalId, cardIndex);
            cardHtml += this.buildDesktopCardView(product, imageUrl, sku, formattedPrice, price, isSelected, showEditButton, previewEyeIcon, modalId, cardIndex);
            cardHtml += `</div>`;

            // Safely create modal HTML
            let modalHTML = '';
            if (showEditButton && this.modalManager && typeof this.modalManager.createProductModalHTML === 'function') {
                try {
                    modalHTML = this.modalManager.createProductModalHTML(product, cardTitle, cardData, modalId);
                } catch (modalError) {
                    console.error('Error creating modal HTML:', modalError);
                    modalHTML = '';
                }
            }

            return cardHtml + modalHTML;

        } catch (error) {
            console.error('Error in createProductCardHTML:', error);
            return `<div class="product-card error-card p-4 text-red-500">Error rendering product card</div>`;
        }
    }

    buildMobileCardView(product, imageUrl, sku, formattedPrice, price, isSelected, isUnavailable, showEditButton, previewEyeIcon, modalId, cardIndex) {
        return `
            <div class="block md:hidden">
                <div class="flex flex-row">
                    <div class="absolute top-2 right-2 z-20 checkmark-container">
                        <div class="h-6 w-6 ${isSelected ? 'bg-[#00B588]' : 'bg-white border-2 border-gray'} rounded-full flex items-center justify-center shadow-md">
                            <svg class="h-5 w-5 text-white checkmark-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: ${isSelected ? 'block' : 'none'};">
                                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                        </div>
                    </div>
                    ${previewEyeIcon}
                    ${showEditButton ? this.buildEditButtonHTML(modalId, product.id, cardIndex, 'mobile') : ''}
                    <div class="w-1/3 p-3">
                        <div class="aspect-square bg-gray-50 flex items-center justify-center rounded-md overflow-hidden relative image-container image-loading">
                            <img src="${imageUrl}" alt="${product.title ? product.title.replace(/"/g, '&quot;') : 'Product Image'}" class="w-full h-full object-contain" loading="lazy">
                            ${isUnavailable ? `<div class="absolute inset-0 flex items-center justify-center z-16"><div class="mobile-coming-soon-badge text-white px-2 py-1 rounded-full transform -rotate-30 font-bold text-mobile-xxs">Coming Soon</div></div>` : ''}
                        </div>
                    </div>
                    <div class="w-3/5 p-3 flex flex-col justify-center">
                        <h3 class="text-sm font-medium text-gray-900 overflow-hidden product-title">${product.title}</h3>
                        <p class="text-xs text-gray-500 mt-1">${sku}</p>
                        ${price > 0 ? `<p class="text-sm font-semibold mt-1">${formattedPrice}</p>` : ''}
                    </div>
                </div>
                ${isUnavailable ? `<div class="absolute inset-0 mobile-image-overlay"></div>` : ''}
            </div>`;
    }

    buildDesktopCardView(product, imageUrl, sku, formattedPrice, price, isSelected, showEditButton, previewEyeIcon, modalId, cardIndex) {
        return `
            <div class="hidden md:block p-3">
                <div class="absolute top-2 right-2 z-20 checkmark-container">
                    <div class="h-6 w-6 ${isSelected ? 'bg-[#00B588]' : 'bg-white border-2 border-gray'} rounded-full flex items-center justify-center shadow-md">
                        <svg class="h-5 w-5 text-white checkmark-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: ${isSelected ? 'block' : 'none'};">
                            <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                    </div>
                </div>
                ${previewEyeIcon}
                ${showEditButton ? this.buildEditButtonHTML(modalId, product.id, cardIndex, 'desktop') : ''}
                <div class="w-full aspect-square bg-gray-50 mb-2 flex items-center justify-center rounded-md overflow-hidden image-container image-loading">
                    <img src="${imageUrl}" alt="${product.title ? product.title.replace(/"/g, '&quot;') : 'Product Image'}" class="w-full h-full object-contain" loading="lazy">
                </div>
                <h3 class="text-sm font-medium text-gray-900 overflow-hidden product-title min-h-[2.5rem]">${product.title}</h3>
                <p class="text-xs text-gray-500 mt-1">${sku}</p>
                ${price > 0 ? `<p class="text-sm font-semibold mt-1">${formattedPrice}</p>` : ''}
            </div>`;
    }

    buildEditButtonHTML(modalId, productId, cardIndex, type) {
        return `
            <div class="absolute top-10 right-2 z-20">
                <button class="w-6 h-6 flex items-center justify-center transition-transform duration-300 hover:scale-110 cursor-pointer edit-options-btn" data-modal-target="${modalId}">
                    <svg class="w-6 h-6 text-gray-600" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <g>
                            <defs><clipPath id="clip-path-${type}-${productId}-${cardIndex}"><rect width="24" height="24" fill="none"></rect></clipPath></defs>
                            <rect width="24" height="24" fill="none"></rect>
                            <g clip-path="url(#clip-path-${type}-${productId}-${cardIndex})">
                                <path d="M0,2A2,2,0,0,1,2,0H4A2,2,0,0,1,6,2V18L3,23,0,18Z" transform="translate(18.01 2.247) rotate(45)" fill="none" stroke="currentColor" stroke-miterlimit="10" stroke-width="1.2"></path>
                                <path d="M0,1H5" transform="translate(15.536 5.429) rotate(45)" fill="none" stroke="currentColor" stroke-linecap="square" stroke-miterlimit="10" stroke-width="1.2"></path>
                            </g>
                        </g>
                    </svg>
                </button>
            </div>`;
    }

    generatePreviewEyeIcon(product) {
        return `
            <div class="absolute inset-0 z-10 preview-eye-icon flex items-center justify-center pointer-events-none">
                <div class="md:w-36 md:h-36 flex items-center justify-center pointer-events-auto preview-eye-btn" data-product-id="${product.id}" data-product-handle="${product.handle}">
                    <div class="w-12 h-12 bg-white backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 pointer-events-none">
                        <svg class="w-8 h-8 text-gray-600 hover:text-gray-800 transition-colors duration-200 pointer-events-none" viewBox="0 0 512 512" fill="currentColor">
                            <path d="M503.698,231.895c-28.735-36.843-65.956-67.318-107.637-88.128c-42.548-21.243-88.321-32.265-136.104-32.843 c-1.316-0.036-6.6-0.036-7.916,0c-47.782,0.579-93.556,11.6-136.104,32.843c-41.681,20.81-78.9,51.284-107.636,88.128 c-11.07,14.193-11.07,34.018,0,48.211c28.735,36.843,65.955,67.318,107.636,88.128c42.548,21.243,88.321,32.265,136.104,32.843 c1.316,0.036,6.6,0.036,7.916,0c47.782-0.579,93.556-11.6,136.104-32.843c41.681-20.81,78.901-51.284,107.637-88.128 C514.768,265.911,514.768,246.088,503.698,231.895z M256,380.303c-68.542,0-124.304-55.762-124.304-124.304 S187.458,131.696,256,131.696S380.304,187.458,380.304,256S324.542,380.303,256,380.303z"/>
                            <path d="M256,202.804c-29.332,0-53.195,23.863-53.195,53.195s23.863,53.195,53.195,53.195s53.195-23.863,53.195-53.195 C309.196,226.667,285.333,202.804,256,202.804z M256,288.367c-17.847,0-32.368-14.519-32.368-32.368 c0-17.848,14.519-32.368,32.368-32.368c17.847,0,32.367,14.519,32.367,32.367C288.368,273.848,273.847,288.367,256,288.367z"/>
                        </svg>
                    </div>
                </div>
            </div>`;
    }

    buildCardSelectionHeaderHTML() {
        const context = this.manager.state.context.context;
        const hoverStrokeColor = 'group-hover:stroke-[#00B588]'; // Target color #00B588
        const transitionClass = 'transition-colors duration-200'; // Optional smooth transition

        return `
            <div class="px-4">
                <div class="md:hidden bg-[#fafafa] z-50" id="mobile-action-bar">
                    <div class="flex justify-between items-center px-4 py-3">
                        <div class="text-left"><span id="subtotal-amount" class="text-lg font-semibold">$0.00</span></div>
                        <div>
                            <button type="button" id="share-btn" class="flex items-center justify-center p-2.5 bg-gray-100 text-gray-500 rounded-md font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                <svg viewBox="-2.4 -2.4 28.80 28.80" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8"><g stroke-width="0"></g><g stroke-linecap="round" stroke-linejoin="round"></g><g><path d="M20 13V17.5C20 20.5577 16 20.5 12 20.5C8 20.5 4 20.5577 4 17.5V13M12 3L12 15M12 3L16 7M12 3L8 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></g></svg>
                            </button>
                        </div>
                        <div class="flex items-center space-x-3">
                            <button type="button" id="add-to-cart-btn" class="group flex items-center px-4 py-2 btn-green text-white rounded-md btn-green:hover transition-colors font-medium">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="cart-svg h-5 w-5 mr-1 stroke-white group-hover:stroke-[#00B588] transition-colors duration-200"><g stroke-width="0"></g><g stroke-linecap="round" stroke-linejoin="round"></g><g><g class="cart-path"><path d="M6.29977 5H21L19 12H7.37671M20 16H8L6 3H3M9 20C9 20.5523 8.55228 21 8 21C7.44772 21 7 20.5523 7 20C7 19.4477 7.44772 19 8 19C8.55228 19 9 19.4477 9 20ZM20 20C20 20.5523 19.5523 21 19 21C18.4477 21 18 20.5523 18 20C18 19.4477 18.4477 19 19 19C19.5523 19 20 19.4477 20 20Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></g></g></svg> Add to Cart
                            </button>
                        </div>
                    </div>
                </div>
                <div class="hidden md:flex justify-between items-center pb-6">
                    <div>
                        <h2 class="text-xl font-bold text-gray-900">${context['Device Make']} ${context['Device Model']}</h2>
                        <p class="text-lg text-gray-600 mb-4">${context['environment']} Environment</p>
                    </div>
                    <div class="text-right">
                        <p class="text-xl font-semibold">Subtotal: <span id="subtotal-amount-desktop">$0.00</span></p>
                        <div class="flex items-center mt-2 space-x-2">
                           <button type="button" id="share-btn-desktop" class="flex items-center px-6 py-2 bg-gray-100 text-gray-500 rounded-md font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            <svg viewBox="-2.4 -2.4 28.80 28.80" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1">
                              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                              <g id="SVGRepo_iconCarrier">
                                <path d="M20 13V17.5C20 20.5577 16 20.5 12 20.5C8 20.5 4 20.5577 4 17.5V13M12 3L12 15M12 3L16 7M12 3L8 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                              </g>
                            </svg>
                            Share
                           </button>
                           <button type="button" id="add-to-cart-btn-desktop" class="group flex items-center px-4 py-2 btn-green text-white rounded-md btn-green:hover transition-colors font-medium">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="cart-svg h-5 w-5 mr-1 stroke-white ${hoverStrokeColor} ${transitionClass}">
                              <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                              <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                              <g id="SVGRepo_iconCarrier">
                                <g class="cart-path">
                                  <path d="M6.29977 5H21L19 12H7.37671M20 16H8L6 3H3M9 20C9 20.5523 8.55228 21 8 21C7.44772 21 7 20.5523 7 20C7 19.4477 7.44772 19 8 19C8.55228 19 9 19.4477 9 20ZM20 20C20 20.5523 19.5523 21 19 21C18.4477 21 18 20.5523 18 20C18 19.4477 18.4477 19 19 19C19.5523 19 20 19.4477 20 20Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                              </g>
                            </svg>
                             Add to Cart
                           </button>
                        </div>
                    </div>
                </div>
                <div class="md:hidden h-0 hidden" id="mobile-action-bar-placeholder"></div>
                <div class="mb-8 mt-2 md:mt-0">
                    <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-6" id="main-cards-container">`;
    }

    buildAdditionalCardsHeaderHTML() {
        return `
            <div class="mt-8">
                <h3 class="text-xl font-bold text-gray-900 mb-4">Recommended Add-Ons</h3>
                <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-6" id="additional-cards-container">`;
    }
}
